import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, LogOut, TrendingUp, Settings, Clock, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BlogManager from './BlogManager';
import ConsultationsView from './ConsultationsView';
import LeadsView from './LeadsView';
import FastCashView from './FastCashView';
import SettingsView from './SettingsView';
import AttributionStats from './AttributionStats';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatLastLogin = (iso) => {
  if (!iso) return 'First login';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return 'Unknown';
  }
};

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('blogs');
  const [stats, setStats] = useState({ blogs: 0, consultations: 0 });
  const [previousLogin] = useState(() => localStorage.getItem('adminPreviousLogin'));
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [blogsRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/admin/blogs`, { headers }),
        axios.get(`${API}/admin/consultations`, { headers })
      ]);

      setStats({
        blogs: blogsRes.data.blogs?.length || 0,
        consultations: consultationsRes.data.consultations?.length || 0
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminPreviousLogin');
    onLogout();
    navigate('/manage-bz/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Bizenntrum Admin</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              data-testid="admin-last-login"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: '#6b7280',
                whiteSpace: 'nowrap'
              }}
              title="Timestamp of your previous successful login. Sanity-check this matches your memory."
            >
              <Clock size={14} />
              <span>Last login: <strong style={{ color: '#059669' }}>{formatLastLogin(previousLogin)}</strong></span>
            </div>
            <button onClick={handleLogout} className="btn-secondary" data-testid="admin-logout-btn">
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        <div className="admin-stats">
          <div className="stat-card">
            <FileText size={32} />
            <div>
              <h3>{stats.blogs}</h3>
              <p>Blog Posts</p>
            </div>
          </div>
          <div className="stat-card">
            <Calendar size={32} />
            <div>
              <h3>{stats.consultations}</h3>
              <p>Strategy Call Requests</p>
            </div>
          </div>
        </div>

        <AttributionStats />

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'blogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('blogs')}
          >
            <FileText size={18} /> Blogs
          </button>
          <button
            className={`admin-tab ${activeTab === 'consultations' ? 'active' : ''}`}
            onClick={() => setActiveTab('consultations')}
          >
            <Calendar size={18} /> Strategy Calls
          </button>
          <button
            className={`admin-tab ${activeTab === 'leads' ? 'active' : ''}`}
            onClick={() => setActiveTab('leads')}
            data-testid="admin-tab-leads"
          >
            <FileDown size={18} /> Lead Magnets
          </button>
          <button
            className={`admin-tab ${activeTab === 'fastcash' ? 'active' : ''}`}
            onClick={() => setActiveTab('fastcash')}
          >
            <TrendingUp size={18} /> FastCASH™
          </button>
          <button
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} /> Settings
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'blogs' && <BlogManager onUpdate={fetchStats} />}
          {activeTab === 'consultations' && <ConsultationsView />}
          {activeTab === 'leads' && <LeadsView />}
          {activeTab === 'fastcash' && <FastCashView />}
          {activeTab === 'settings' && <SettingsView />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;